import * as React from "react";
import { Box, Typography, IconButton, InputAdornment, Alert } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import DarkTextField from "./DarkTextField";
import AuthPrimaryButton from "./AuthPrimaryButton";
import PasswordStrengthChecklist from "./PasswordStrengthChecklist";
import { isPasswordValid } from "../utils/passwordRules";
import { AUTH_TEXT_PRIMARY, AUTH_TEXT_SECONDARY, AUTH_ERROR } from "../theme/appTheme";
import { useTranslation } from "../i18n/LanguageContext";

// Password-only step for reset-password/complete — no auto-login afterward,
// the caller redirects to /login on success.
function SetNewPasswordForm({ onSubmit, loading, error, fieldErrors, submitLabel }) {
  const { t } = useTranslation();
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [fieldError, setFieldError] = React.useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password) {
      setFieldError(t("auth.setNewPassword.passwordRequired"));
      return;
    }
    if (!isPasswordValid(password)) {
      setFieldError(t("auth.setNewPassword.passwordRequirements"));
      return;
    }
    if (password !== confirmPassword) {
      setFieldError(t("auth.setNewPassword.passwordsMismatch"));
      return;
    }
    setFieldError("");
    onSubmit(password);
  };

  const visibilityToggle = (
    <InputAdornment position="end">
      <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" size="small" sx={{ color: "#6b7280" }}>
        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <form onSubmit={handleSubmit}>
      <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: "1.0625rem", color: AUTH_TEXT_PRIMARY }}>
        {t("auth.setNewPassword.title")}
      </Typography>
      <Typography sx={{ mb: 3, fontSize: "0.8125rem", color: AUTH_TEXT_SECONDARY }}>
        {t("auth.setNewPassword.subtitle")}
      </Typography>

      <DarkTextField
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        type={showPassword ? "text" : "password"}
        placeholder={t("auth.setNewPassword.newPasswordPlaceholder")}
        InputProps={{ endAdornment: visibilityToggle }}
      />
      <PasswordStrengthChecklist password={password} />
      {Array.isArray(fieldErrors) && fieldErrors.length > 0 && (
        <Box component="ul" sx={{ m: 0, mb: 1, pl: 2.25 }}>
          {fieldErrors.map((msg, i) => (
            <Typography key={i} component="li" variant="caption" sx={{ color: AUTH_ERROR }}>
              {msg}
            </Typography>
          ))}
        </Box>
      )}
      <DarkTextField
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        type={showPassword ? "text" : "password"}
        placeholder={t("auth.setNewPassword.confirmPasswordPlaceholder")}
        sx={{ mt: 1.5 }}
      />

      {fieldError && (
        <Typography variant="caption" sx={{ color: AUTH_ERROR, mt: 0.5, display: "block" }}>
          {fieldError}
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <AuthPrimaryButton type="submit" disabled={loading || !isPasswordValid(password)} sx={{ mt: 2.5 }}>
        {loading ? t("auth.setNewPassword.submitting") : submitLabel || t("auth.setNewPassword.submit")}
      </AuthPrimaryButton>
    </form>
  );
}

export default SetNewPasswordForm;
